import { clientId, userPoolId } from "../../config";
import { mapRegistrationApiErrors } from "../utils/mapAuthApiErrors/maRegistrationApiErrors";
import { mapSignInApiErrors } from "../utils/mapAuthApiErrors/mapSignInApiErrors";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  type IAuthenticationDetailsData,
  type ICognitoUserData,
  type ISignUpResult,
  type ICognitoUserPoolData,
} from "amazon-cognito-identity-js";
import type {
  AccountSession,
  FinalUser,
  RegistrationData,
  SignedInUser,
} from "../types/auth";
import { omit } from "../utils/omit";
import {
  getValidationUser,
  removeValidationUser,
  setAccountSession,
  setUserAccount,
  setValidationUser,
} from "../utils/auth";
import { Role } from "../types/roles";

export class Cognito {
  private static instance: Cognito;
  private poolData: ICognitoUserPoolData;
  private userPool: CognitoUserPool;

  private constructor() {
    this.poolData = {
      UserPoolId: userPoolId,
      ClientId: clientId,
    };

    this.userPool = new CognitoUserPool(this.poolData);
  }

  private getSignUpAttributes(
    data: Partial<RegistrationData>
  ): CognitoUserAttribute[] {
    const attributes: CognitoUserAttribute[] = [];

    Object.entries(data).forEach(([key, value]) => {
      if (value) {
        const attribute: CognitoUserAttribute = new CognitoUserAttribute({
          Name: key,
          Value: value,
        });

        attributes.push(attribute);
      }
    });

    return attributes;
  }

  private currentUserAdapter(
    attributes: CognitoUserAttribute[] = []
  ): SignedInUser {
    const finalAttributes: Record<string, string | boolean> = {};

    attributes.forEach(({ Name, Value }) => {
      if (Name === "email_verified") {
        finalAttributes[Name] = Value.toLocaleLowerCase() === "true";
      }
      finalAttributes[Name as keyof SignedInUser] = Value;
    });

    return {
      email: finalAttributes.email as string,
      email_verified: finalAttributes.email_verified as boolean,
      family_name: finalAttributes.family_name as string,
      given_name: finalAttributes.given_name as string,
      sub: finalAttributes.sub as string,
      role: finalAttributes.role as Role,
      profile: finalAttributes.profille as string,
    };
  }

  private async getCurrentUser(): Promise<SignedInUser | null> {
    const currentUser = this.userPool.getCurrentUser();

    if (!currentUser) {
      return new Promise((resolve) => {
        resolve(null);
      });
    }
    return new Promise<SignedInUser>((resolve, reject) => {
      currentUser.getSession((err: Error) => {
        if (err) {
          reject(new Error("Error retrieving user"));
        }

        currentUser.getUserAttributes((error, attributes) => {
          if (error) {
            reject(new Error("Error retrieving user"));
          }

          const attrs = this.currentUserAdapter(attributes);

          resolve(attrs);
        });
      });
    });
  }

  static getInstance(): Cognito {
    if (!this.instance) {
      Cognito.instance = new Cognito();
    }

    return Cognito.instance;
  }

  public async signIn(username: string, password: string): Promise<FinalUser> {
    const authenticationData: IAuthenticationDetailsData = {
      Username: username,
      Password: password,
    };
    const authenticationDetails: AuthenticationDetails =
      new AuthenticationDetails(authenticationData);

    const user: ICognitoUserData = {
      Username: username,
      Pool: this.userPool,
    };
    const cognitoUser: CognitoUser = new CognitoUser(user);

    return new Promise<AccountSession>((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onFailure(err) {
          const errorMessage = mapSignInApiErrors(err);

          reject(new Error(errorMessage));
        },
        onSuccess(result) {
          const tokens: AccountSession = {
            token: result.getAccessToken().getJwtToken(),
            refreshToken: result.getRefreshToken().getToken(),
          };

          setAccountSession(tokens);
          resolve(tokens);
        },
      });
    }).then(async (session: AccountSession) => {
      const data = await this.getCurrentUser();

      if (!data) {
        throw new Error("Error retrieving user");
      }

      setUserAccount(data);

      const user: FinalUser = {
        session,
        user: data,
      };
      
      return user;
    });
  }

  public async signup(
    username: string,
    password: string,
    userAttributes: RegistrationData
  ): Promise<ISignUpResult> {
    const user: ICognitoUserData = {
      Username: username,
      Pool: this.userPool,
    };
    const cognitoUser: CognitoUser = new CognitoUser(user);
    const signUpAttributes = omit(userAttributes, [
      "groupCode",
      "password",
      "phone_number",
    ]);
    const attributes: CognitoUserAttribute[] =
      this.getSignUpAttributes(signUpAttributes);

    return new Promise<ISignUpResult>((resolve, reject) => {
      this.userPool.signUp(username, password, attributes, [], (err, data) => {
        if (err) {
          const message = mapRegistrationApiErrors(err);

          reject(new Error(message));

          return;
        }

        if (data) {
          setValidationUser(cognitoUser.getUsername());

          resolve(data);
        }
      });
    });
  }

  public async confirmRegistration(verificationCode: string) {
    const username = getValidationUser() ?? "";
    const user: ICognitoUserData = {
      Username: username,
      Pool: this.userPool,
    };
    const cognitoUser: CognitoUser = new CognitoUser(user);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(verificationCode, true, (err, data) => {
        if (err) {
          const message = mapRegistrationApiErrors(err);

          reject(new Error(message));

          return;
        }

        removeValidationUser();

        resolve(data);
      });
    });
  }

  public async resendVerificationCode(): Promise<unknown> {
    const username = getValidationUser() ?? "";
    const user: ICognitoUserData = {
      Username: username,
      Pool: this.userPool,
    };
    const cognitoUser: CognitoUser = new CognitoUser(user);

    return new Promise((resolve, reject) => {
      cognitoUser.resendConfirmationCode((err, data) => {
        if (err) {
          const message = mapRegistrationApiErrors(err);

          reject(new Error(message));

          return;
        }

        resolve(data);
      });
    });
  }
}
